<script setup>
import { useAnalysisSajuContext } from '@/composables/saju-insight/useAnalysisSajuContext';
const context = useAnalysisSajuContext();
let alive = true;
let harmonyRequestId = 0;
onBeforeUnmount(() => { alive = false; harmonyRequestId += 1; });
const isCurrent = () => alive && (!context || context.isActive());

import { DEV_MODE } from '@/config/dev-mode';
import { CONTENTS } from './constants';

import { fetchHarmonyServiceAPI } from '@/api/baseServiceApi.js';
import { transformHarmonyResponse } from '@/utils/harmonyDataTransformer.js';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { usePaymentsStore } from '@/store/modules/payments';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';

import ContentsHarmony01 from './ContentsHarmony01.vue';
import ContentsHarmony02 from './ContentsHarmony02.vue';
import ContentsHarmony03 from './ContentsHarmony03.vue';
import ContentsHarmony04 from './ContentsHarmony04.vue';

import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import { isObjectEmpty } from '@/composables/functions';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { addContentLogAPI } from '@/api/logServiceApi';

const HARMONY_CATEGORIES = [
  { id: 'lover', label: '연인', emoji: '💖' },
  { id: 'friend', label: '친구', emoji: '🤝' },
  { id: 'business', label: '비즈니스', emoji: '💼' },
  { id: 'family', label: '가족', emoji: '🏠' },
];
const CATEGORY_API_MAP = {
  lover: 'LOVER',
  friend: 'FRIEND',
  business: 'BUSINESS',
  family: 'FAMILY',
};
const ANALYSIS_TYPE_ID = 38;

const props = defineProps({
  hasPermission: {
    type: Boolean,
  },
});
const emit = defineEmits(['goPopAnalysis']);

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const primarySajuStore = usePrimarySajuStore();

/** DetailPanelHarmony에서 카테고리+멤버 기반으로 판별한 결제 여부를 store에서 참조 */
const effectivePermission = computed(() => store.harmonyIsPurchased);
/** 구독이 이 조합을 커버: 구독중 && base(본인 자리)가 내사주.
 *  BE 구독 커버가 base(guestUserId) 기준이라 FE도 base로 판정. */
const subscriptionCovers = computed(
  () => paymentsStore.isSubscribed && primarySajuStore.isMySaju(harmonyParams.value?.guestUserId),
);
/** 표시용 권한: 구매 OR 구독 커버 → 전체 열람 가능 */
const canViewFull = computed(() => effectivePermission.value || subscriptionCovers.value);

// =============== 종합조언/활동추천 페이지 표시 여부 ===============
/** 3인/4인 궁합은 아직 종합조언·활동추천 데이터가 미지원 → 해당 페이지 동적 제외 */
const showAdvicePage = computed(() => {
  const hData = dataStore.harmonyData;
  return !!(hData?.종합조언 || hData?.함께하면좋은활동);
});

/** 순차 페이지 번호 → 컨텐츠 컴포넌트 번호 매핑 */
const pageContentMap = computed(() => {
  if (showAdvicePage.value) return { 1: 1, 2: 2, 3: 3, 4: 4 };
  return { 1: 1, 2: 2, 3: 4 };
});

/** 현재 페이지에 해당하는 컨텐츠 번호 */
const currentContent = computed(() => pageContentMap.value[indexNo.value] ?? 1);

/** 총 페이지 수 */
const totalPages = computed(() => Object.keys(pageContentMap.value).length);

/** 동적 목차 (종합조언 미지원 시 해당 항목 제외) */
const dynamicContents = computed(() => {
  if (showAdvicePage.value) return CONTENTS;
  return [
    { id: 1, title: '1. 궁합 종합 분석' },
    { id: 2, title: '2. 지표 상세 분석' },
    { id: 3, title: '' },
  ];
});

// =============== 페이지 ===============
/** 페이지 이동 */
const moveToPage = (id) => {
  if (id > 1 && !canViewFull.value) {
    store.triggerHarmonyPaymentPopup();
    return;
  }
  indexNo.value = id;
};

/** index값으로 페이지 이동 (목차 클릭 시) */
const updateIndexNo = (id) => {
  addContentLogAPI({
    contentType: effectivePermission.value ? 'ap_harmony_page_paid_opt3' : 'ap_harmony_page_free_opt3',
    option3: String(id),
  });
  moveToPage(id);
};

// 인덱스
const indexNo = ref(1);
watch(indexNo, (newIndex) => {
  store.setNangmanHarmonyIndex(newIndex);
});
onUnmounted(() => {
  store.setNangmanHarmonyIndex(1);
});

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const item = dynamicContents.value[newIdx];
    if (item) moveToPage(item.id);
  },
  { immediate: true },
);

/** 표지로 이동 */
const goToOverView = () => {
  store.requestHarmonyResetSelection();
  store.clickMainMenu('/overviewNangmanHarmony');
};

/** content1 결과보기 클릭 → 미구매 시 결제 팝업 트리거 */
const onViewCategory = (categoryId) => {
  if (!canViewFull.value) {
    store.triggerHarmonyPaymentPopup();
  }
};

// =============== 궁합 파라미터 ===============
/** 현재 결제 데이터에서 API 파라미터 추출 */
const harmonyParams = computed(() => {
  const paymentData = store.harmonyPaymentData;
  if (!paymentData?.category || !paymentData?.persons?.length) return null;

  // sortOrder가 가장 낮은 사람이 본인
  const sorted = [...paymentData.persons].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const myId = Number(sorted[0].guestUserId);
  if (context && myId !== Number(context.saju.value?.guestUserId)) return null;
  const memberIds = sorted.map((p) => Number(p.guestUserId));

  return {
    guestUserId: myId,
    category: paymentData.category,
    memberIds,
    analysisLogId: store.harmonyMatchedLog?.analysisLogId ?? null,
  };
});

// =============== 평가하기 데이터 ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

const getHarmonyRate = async () => {
  if (!effectivePermission.value || !harmonyParams.value?.analysisLogId) return;
  try {
    const params = {
      serviceName: 'NANGMAN_HARMONY',
      objectKey: `${harmonyParams.value.analysisLogId}`,
    };
    const result = await getDailyFortuneRateAPI(params);
    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

const postHarmonyRate = async (score) => {
  if (!harmonyParams.value?.analysisLogId) return;
  try {
    const params = {
      score,
      serviceName: 'NANGMAN_HARMONY',
      objectKey: `${harmonyParams.value.analysisLogId}`,
    };
    const result = await postDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      getHarmonyRate();
    }
  } catch (error) {}
};

onMounted(() => {
  getHarmonyRate();
});

// =============== 궁합 데이터 및 상태 ===============

const isFetchingHarmony = ref(false);

const isReadyToShow = computed(() => {
  return !isFetchingHarmony.value && !isObjectEmpty(dataStore.harmonyData);
});

/** 궁합 데이터 fetch */
const fetchHarmonyData = async (suggestionFlag = null) => {
  if (!isCurrent()) return;
  const request = ++harmonyRequestId;
  if (!harmonyParams.value) return;
  isFetchingHarmony.value = true;
  try {
    // 권한(구매 OR 구독+내사주 base)이면 전체(flag 3), 아니면 무료 미리보기(flag 1).
    // BE는 비권한 조합에 flag 3 요청 시 403을 주므로 flag을 canViewFull과 일치시킨다.
    const flag = suggestionFlag ?? (canViewFull.value ? 3 : 1);
    const params = { ...harmonyParams.value, suggestionFlag: flag };
    const result = await fetchHarmonyServiceAPI(params);
    if (!isCurrent() || request !== harmonyRequestId) return;
    const transformed = transformHarmonyResponse(result?.data, params.category);
    dataStore.setHarmonyData(transformed);
    DEV_MODE && console.log('fetchHarmonyData 완료:', transformed);
    DEV_MODE && console.log('fetchHarmonyData 원본:', result?.data);
  } catch (error) {
    DEV_MODE && console.error('fetchHarmonyData 실패:', error);
  } finally {
    if (isCurrent() && request === harmonyRequestId) isFetchingHarmony.value = false;
  }
};

/** 마운트 시 결제 데이터가 있으면 바로 fetch (cover→detail 전환 시) */
onMounted(async () => {
  DEV_MODE && console.log('[DetailPageHarmony] onMounted', {
    effectivePermission: effectivePermission.value,
    harmonyParams: harmonyParams.value,
  });
  if (harmonyParams.value) {
    await fetchHarmonyData();
  }
});

/** 이미 마운트된 상태에서 다른 카드 클릭 시 (DetailPanel 궁합목록에서 카드 변경) */
watch(
  () => store.harmonyPaymentData,
  async () => {
    harmonyRequestId += 1;
    if (isCurrent()) dataStore.setHarmonyData({});
    DEV_MODE && console.log('[DetailPageHarmony] paymentData changed (while mounted)');
    if (harmonyParams.value) {
      // 다른 관계/카드로 전환 시 첫 페이지부터 보기 (모바일 동작과 일치)
      indexNo.value = 1;
      ratingResult.value = 0;
      ratingStatus.value = false;
      await fetchHarmonyData();
      getHarmonyRate();
    }
  },
);

/** 결제 완료 후 (effectivePermission false→true) 자동 fetch */
watch(
  effectivePermission,
  async (newValue, oldValue) => {
    if (!oldValue && newValue && harmonyParams.value) {
      await fetchHarmonyData();
    }
  },
);

// =============== 다른 관계 살펴보기 플로팅 버튼 ===============
const showExploreMenu = ref(false);

/** 현재 멤버 조합으로 각 카테고리별 구매 여부 확인 */
const purchasedCategoriesForPair = computed(() => {
  const matched = store.harmonyMatchedLog;
  if (!matched?.members?.length) return new Set();
  const currentIds = [...matched.members].map((m) => Number(m.guestUserId)).sort((a, b) => a - b);
  const purchased = new Set();
  for (const [catId, apiCat] of Object.entries(CATEGORY_API_MAP)) {
    const cacheKey = `${ANALYSIS_TYPE_ID}_${apiCat}`;
    const logs = store.analysisLogByType[cacheKey] || [];
    const has = logs.some((log) => {
      const logIds = [...(log.members || [])].map((m) => Number(m.guestUserId)).sort((a, b) => a - b);
      return logIds.length === currentIds.length && currentIds.every((id, i) => id === logIds[i]);
    });
    if (has) purchased.add(catId);
  }
  return purchased;
});

const onClickExploreCategory = (cat) => {
  if (purchasedCategoriesForPair.value.has(cat.id)) return;
  addContentLogAPI({ contentType: 'ap_harmony_btn_explore_category', option3: cat.label });
  showExploreMenu.value = false;
  // 구독이 이 조합을 커버하면(구매 여부 무관) 결제 없이 카테고리만 전환 → harmonyPaymentData watch가 재조회
  if (subscriptionCovers.value) {
    const paymentData = store.harmonyPaymentData;
    store.setHarmonyPaymentData({
      category: CATEGORY_API_MAP[cat.id],
      memberGuestUserIds: paymentData?.memberGuestUserIds || harmonyParams.value?.memberIds || [],
      persons: paymentData?.persons || [],
    });
    return;
  }
  store.triggerHarmonyCategoryPayment(cat);
};

const toggleExploreMenu = () => {
  if (!showExploreMenu.value) {
    addContentLogAPI({ contentType: 'ap_harmony_btn_explore_menu' });
  }
  showExploreMenu.value = !showExploreMenu.value;
};
</script>

<template>
  <div v-if="store.selectedMenu.endsWith('nangman-harmony')" class="full-height">
    <div v-if="isFetchingHarmony" class="analysis__loading-overlay full-height">
      <div class="loading-olverlay__img"></div>
      낭만궁합 분석중입니다!
    </div>

    <ContentsHarmony01
      v-if="currentContent === 1 && !isFetchingHarmony"
      class="full-height"
      :hData="dataStore.harmonyData"
      :userName="store.userName"
      :categoryId="store.harmonyPaymentData?.category"
      :hasPermission="canViewFull"
    />

    <ContentsHarmony02
      v-if="currentContent === 2 && !isFetchingHarmony"
      class="full-height"
      :hData="dataStore.harmonyData"
      :userName="store.userName"
      :categoryId="store.harmonyPaymentData?.category"
      :hasPermission="canViewFull"
    />

    <ContentsHarmony03
      v-if="currentContent === 3 && !isFetchingHarmony"
      class="full-height"
      :hData="dataStore.harmonyData"
      :userName="store.userName"
      :hasPermission="canViewFull"
      :subscriptionCovers="subscriptionCovers"
    />

    <ContentsHarmony04
      v-if="currentContent === 4 && !isFetchingHarmony"
      class="full-height"
      :hData="dataStore.harmonyData"
      :userName="store.userName"
      :hasPermission="canViewFull"
      :ratingResult="ratingResult"
      :ratingStatus="ratingStatus"
      @postRate="postHarmonyRate"
      @goToOverView="goToOverView"
    />
  </div>

  <!-- 다른 관계 살펴보기 플로팅 버튼 (구매/구독 열람 시) -->
  <div
    v-if="canViewFull && !isFetchingHarmony"
    class="explore-float-wrap"
  >
    <Transition name="explore-menu-fade">
      <div v-if="showExploreMenu" class="explore-float-menu">
        <button
          v-for="cat in HARMONY_CATEGORIES"
          :key="cat.id"
          class="explore-float-menu__item"
          :class="{
            'explore-float-menu__item--active': purchasedCategoriesForPair.has(cat.id),
            'explore-float-menu__item--disabled': purchasedCategoriesForPair.has(cat.id),
          }"
          @click="onClickExploreCategory(cat)"
        >
          <span class="explore-float-menu__emoji">{{ cat.emoji }}</span>
          <span class="explore-float-menu__label">{{ cat.label }}</span>
        </button>
      </div>
    </Transition>
    <button class="explore-float-btn" @click="toggleExploreMenu">
      <svg
        class="explore-float-btn__icon"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="6.5" cy="6.5" r="5" stroke="white" stroke-width="1.5" />
        <line x1="10.35" y1="10.35" x2="14.15" y2="14.15" stroke="white" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      다른 관계 살펴보기
    </button>
  </div>
  <div v-if="showExploreMenu" class="explore-float-backdrop" @click="showExploreMenu = false"></div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="dynamicContents"
      :curPage="indexNo"
      :lastPage="totalPages"
      :hideToc="true"
      :disablePrev="true"
      :loopToFirst="true"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

/* 다른 관계 살펴보기 플로팅 버튼 */
.explore-float-backdrop {
  position: fixed;
  inset: 0;
  z-index: 48;
}

.explore-float-wrap {
  position: absolute;
  bottom: 50px;
  right: 16px;
  z-index: 49;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.explore-float-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 12px;
  border-radius: 60px;
  border: none;
  background: #ff6b6b;
  color: #fff;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  box-shadow: 2px 2px 10px 0px rgba(255, 107, 107, 0.2);
  cursor: pointer;
  white-space: nowrap;

  &__icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
}

.explore-float-menu {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-end;

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 60px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: #fff;
    box-shadow: 2px 2px 10px 0px rgba(143, 143, 143, 0.2);
    cursor: pointer;
    white-space: nowrap;
    min-width: 92px;

    &--active {
      background: #f8f8f8;
      border-color: transparent;
    }

    &--disabled {
      cursor: default;
      opacity: 1;
    }
  }

  &__emoji {
    font-size: 18px;
    line-height: 18px;
  }

  &__label {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: var(--Gray-600, #757575);
  }

  .explore-float-menu__item--active .explore-float-menu__label {
    color: var(--Gray-500, #949494);
  }
}

/* 트랜지션 */
.explore-menu-fade-enter-active,
.explore-menu-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.explore-menu-fade-enter-from,
.explore-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
